import React from 'react'
import { getPlaceholderImageUrl } from '@utils/image'

const calculatePrice = (item: any): number => {
  if (!item || !item.campaign_product) {
    return 0
  }
  const basePrice = parseFloat(String(item.campaign_product.price)) || 0
  const variantAdjustment = parseFloat(String(item.variant?.price_adjustment)) || 0
  return (basePrice + variantAdjustment) * (item.quantity || 1)
}

const formatCurrency = (amount: string | number, currency: string = 'USD'): string => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(num || 0)
}

const getItemVariants = (item: any): Array<{ name: string; value: string }> => {
  if (!item?.variant?.options) {
    return []
  }
  return item.variant.options
    .filter((option: any) => !option.is_deleted)
    .map((option: any) => ({
      name: option.name,
      value: option.value,
    }))
}

const PLACEHOLDER_IMAGE = getPlaceholderImageUrl('medium')

const getItemImageUrl = (item: any): string => {
  const cp = item?.campaign_product
  // Order items keep their images on the item's `product`, not on campaign_product,
  // whose `product` is only an id.
  const product = cp?.product ?? item?.product
  return (
    cp?.image?.file?.medium ||
    cp?.image?.file?.original ||
    (typeof cp?.image === 'string' ? cp.image : null) ||
    product?.default_image?.file?.medium ||
    product?.default_image?.file?.original ||
    product?.images?.[0]?.file?.medium ||
    product?.images?.[0]?.file?.original ||
    (typeof product?.images?.[0]?.file === 'string' ? product.images[0].file : null) ||
    PLACEHOLDER_IMAGE
  )
}

const setFallbackImageUrl = (event: React.SyntheticEvent<HTMLImageElement>): void => {
  ;(event.target as HTMLImageElement).src = PLACEHOLDER_IMAGE
}

// Types
interface PaymentSource {
  id: number
  isDefault: boolean
  canUpdate: boolean
  isCard: boolean
  source: string
  type: string
  last4: string
  expDate: string
  // Additional fields from API (optional for compatibility)
  card_type?: string
  last_four?: string
  expiry_month?: number
  expiry_year?: number
  is_default?: boolean
  payment_source?: number
  cc_type?: string
  cc_last4?: string
  cc_exp_date?: string
  can_update?: boolean
}

// Extend CartItem to include missing properties
interface ExtendedCartItem {
  id?: string
  campaign_product: {
    id: number
    name: string
    price: number
    image?: string
    currency?: string
    max_quantity?: number
    bundled_variants?: Array<{
      variant: { id: number; name: string }
      quantity: number
    }>
  }
  variant?: {
    id: number
    name: string
    price_adjustment?: number
    options?: Array<{
      name: string
      value: string
      is_deleted?: boolean
    }>
  }
  quantity: number
  buy_now_clicked?: boolean
}

enum OrderPaymentOptions {
  New = 'new',
  Default = 'default',
}

// Detect card brand from card number (for CVV length validation)
const detectCardBrand = (number: string): string => {
  const n = number.replace(/\D/g, '')
  if (/^4/.test(n)) {
    return 'visa'
  }
  if (/^5[1-5]/.test(n) || /^2[2-7]/.test(n)) {
    return 'mastercard'
  }
  if (/^3[47]/.test(n)) {
    return 'amex'
  }
  if (/^6(?:011|5)/.test(n)) {
    return 'discover'
  }
  return 'unknown'
}

export {
  calculatePrice,
  formatCurrency,
  getItemVariants,
  getItemImageUrl,
  setFallbackImageUrl,
  detectCardBrand,
  OrderPaymentOptions,
}
export type { PaymentSource, ExtendedCartItem }
